import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select, {
  StylesConfig,
  ControlProps,
  MenuProps,
  OptionProps,
  CSSObjectWithLabel,
} from "react-select";
import { Play, Check } from "lucide-react";
import {
  InteractiveMaterialResponseDto,
  InteractiveMaterialResponseDtoGeneratedQuizQuestionsItem,
  TestQuestion,
} from "@packages/api/madlenAPI.schemas";
import { Text, Button } from "@core/index";
import {
  VideoSection,
  ContentSection,
  EditButtons,
  SaveCancelButtons,
  useEditState,
  useVideoSection,
  formatTimestamp,
} from "./utils";

// Define the option type for react-select
interface OptionType {
  value: string;
  label: string;
}

// Options for react-select
const questionTypeOptions: OptionType[] = [
  { value: "TEST", label: "interactive_material.questions.multipleChoice" },
  { value: "TF", label: "interactive_material.questions.trueFalse" },
];

// Custom styles for react-select with proper types
const customSelectStyles: StylesConfig<OptionType, false> = {
  control: (provided: CSSObjectWithLabel, state: ControlProps<OptionType>) => ({
    ...provided,
    backgroundColor: state.isFocused
      ? "white"
      : document.documentElement.classList.contains("dark")
        ? "#1F2937"
        : "white",
    borderColor: state.isFocused
      ? "#F43F5E"
      : document.documentElement.classList.contains("dark")
        ? "#374151"
        : "#E5E7EB",
    borderRadius: "0.5rem",
    padding: "0.5rem 0.75rem",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(244, 63, 94, 0.5)" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#F43F5E" : "#D1D5DB",
    },
  }),
  menu: (provided: CSSObjectWithLabel, _state: MenuProps<OptionType>) => ({
    ...provided,
    backgroundColor: document.documentElement.classList.contains("dark")
      ? "#1F2937"
      : "white",
    borderRadius: "0.5rem",
    border: document.documentElement.classList.contains("dark")
      ? "1px solid #374151"
      : "1px solid #E5E7EB",
  }),
  option: (provided: CSSObjectWithLabel, state: OptionProps<OptionType>) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#F43F5E"
      : state.isFocused
        ? document.documentElement.classList.contains("dark")
          ? "#374151"
          : "#F3F4F6"
        : "transparent",
    color: state.isSelected
      ? "white"
      : document.documentElement.classList.contains("dark")
        ? "#D1D5DB"
        : "#1F2937",
    padding: "0.5rem 1rem",
    "&:active": {
      backgroundColor: "#F43F5E",
      color: "white",
    },
  }),
  singleValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: document.documentElement.classList.contains("dark")
      ? "#D1D5DB"
      : "#1F2937",
  }),
  placeholder: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: document.documentElement.classList.contains("dark")
      ? "#6B7280"
      : "#9CA3AF",
  }),
};

interface QuizQuestionTabProps {
  material: InteractiveMaterialResponseDto;
  onQuestionsChange?: (
    questions: InteractiveMaterialResponseDtoGeneratedQuizQuestionsItem[],
  ) => void;
}

const QuizQuestionTab = ({
  material,
  onQuestionsChange,
}: QuizQuestionTabProps) => {
  const { t } = useTranslation();
  const questionsListRef = useRef<HTMLDivElement>(null);
  const [questions, setQuestions] = useState<
    InteractiveMaterialResponseDtoGeneratedQuizQuestionsItem[]
  >(material.generatedQuizQuestions || []);

  const videoSection = useVideoSection();

  const {
    editingIndex,
    editingItem,
    setEditingItem,
    handleEdit,
    handleDelete,
    handleSave,
    handleCancel,
    handleCreate,
  } = useEditState({
    items: questions,
    onItemsChange: onQuestionsChange,
    createNewItem: () => ({
      type: "TEST",
      body: "",
      options: { A: "", B: "", C: "", D: "" },
      answer: "A",
      startTime: 0,
    }),
    setItems: setQuestions,
    containerRef: questionsListRef,
  });

  useEffect(() => {
    setQuestions(material.generatedQuizQuestions || []);
  }, [material.generatedQuizQuestions]);

  const handleTimestampClick = (startTime: number) => {
    if (!videoSection.isVideoVisible) {
      videoSection.setIsVideoVisible(true);
      setTimeout(() => {
        const iframe = document.querySelector<HTMLIFrameElement>("iframe");
        if (iframe?.contentWindow) seekAndPlayVideo(iframe, startTime);
      }, 1000);
    } else {
      const iframe = document.querySelector<HTMLIFrameElement>("iframe");
      if (iframe?.contentWindow) seekAndPlayVideo(iframe, startTime);
    }
  };

  const seekAndPlayVideo = (iframe: HTMLIFrameElement, startTime: number) => {
    const message = JSON.stringify({
      event: "command",
      func: "seekTo",
      args: [startTime],
    });
    iframe.contentWindow?.postMessage(message, "*");

    setTimeout(() => {
      const playMessage = JSON.stringify({
        event: "command",
        func: "playVideo",
        args: [],
      });
      iframe.contentWindow?.postMessage(playMessage, "*");
    }, 100);
  };

  return (
    <div className="h-[calc(100vh-20rem)] max-h-[800px] min-h-[600px] flex flex-col lg:flex-row gap-4 relative">
      <VideoSection
        {...videoSection}
        youtubeUrl={material.youtubeUrl}
        generatorMode={material.generatorMode}
      />

      <ContentSection
        title={t("interactive_material.questions.title")}
        onAddNew={handleCreate}
        addButtonLabel={t("interactive_material.questions.addNew")}
        isEmpty={questions.length === 0}
        emptyStateMessage={t("interactive_material.questions.empty")}
        containerRef={questionsListRef}
      >
        {questions.map((item, index) => (
          <div
            key={index}
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200/50 dark:border-gray-700/50 transition-all hover:shadow-md"
          >
            {editingIndex === index && editingItem ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("interactive_material.questions.type")}
                  </label>
                  <Select
                    options={questionTypeOptions}
                    value={questionTypeOptions.find(
                      (option) => option.value === editingItem.type,
                    )}
                    onChange={(selectedOption) =>
                      setEditingItem({
                        ...editingItem,
                        type: (selectedOption?.value || "TEST") as
                          | "TEST"
                          | "TF",
                        options:
                          selectedOption?.value === "TEST"
                            ? { A: "", B: "", C: "", D: "" }
                            : undefined,
                        answer: selectedOption?.value === "TEST" ? "A" : true,
                      })
                    }
                    styles={customSelectStyles}
                    getOptionLabel={(option) => t(option.label)}
                    placeholder={t("interactive_material.questions.selectType")}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("interactive_material.questions.question")}
                  </label>
                  <textarea
                    value={editingItem.body}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        body: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 resize-none"
                    rows={3}
                    placeholder={t(
                      "interactive_material.questions.questionPlaceholder",
                    )}
                  />
                </div>

                {editingItem.type === "TEST" && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t("interactive_material.questions.options")}
                    </label>
                    {Object.entries(
                      (editingItem as TestQuestion).options || {},
                    ).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex flex-col sm:flex-row items-start sm:items-center gap-2"
                      >
                        <Text
                          variant="span"
                          size={"base"}
                          className="font-medium min-w-[24px]"
                        >
                          {key}
                        </Text>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) =>
                            setEditingItem({
                              ...editingItem,
                              options: {
                                ...(editingItem as TestQuestion).options,
                                [key]: e.target.value,
                              },
                            })
                          }
                          className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                          placeholder={t(
                            "interactive_material.questions.optionPlaceholder",
                          )}
                        />
                        <div className="flex items-center justify-end w-full sm:w-auto">
                          <input
                            type="radio"
                            checked={editingItem.answer === key}
                            onChange={() =>
                              setEditingItem({
                                ...editingItem,
                                answer: key,
                              })
                            }
                            className="form-radio text-rose-600"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {editingItem.type === "TF" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("interactive_material.questions.answer")}
                    </label>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          checked={editingItem.answer === true}
                          onChange={() =>
                            setEditingItem({
                              ...editingItem,
                              answer: true,
                            })
                          }
                          className="form-radio text-rose-600"
                        />
                        <Text variant="span" size={"base"} className="ml-2">
                          {t("common.true")}
                        </Text>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          checked={editingItem.answer === false}
                          onChange={() =>
                            setEditingItem({
                              ...editingItem,
                              answer: false,
                            })
                          }
                          className="form-radio text-rose-600"
                        />
                        <Text variant="span" size={"base"} className="ml-2">
                          {t("common.false")}
                        </Text>
                      </label>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-2">
                  <SaveCancelButtons
                    onSave={() => handleSave(index)}
                    onCancel={handleCancel}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
                    <Text
                      variant="span"
                      size={"xs"}
                      weight={"medium"}
                      className="px-2 py-1 rounded bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300"
                    >
                      {item.type === "TEST"
                        ? t("interactive_material.questions.multipleChoice")
                        : t("interactive_material.questions.trueFalse")}
                    </Text>
                    {!!item.startTime &&
                      material.generatorMode === "YOUTUBE" && (
                        <Button
                          onClick={() => handleTimestampClick(item.startTime!)}
                          size="sm"
                          textColor="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300"
                        >
                          <Play size={14} />
                          {formatTimestamp(item.startTime)}
                        </Button>
                      )}
                  </div>
                  <EditButtons
                    onEdit={() => handleEdit(index)}
                    onDelete={() => handleDelete(index)}
                  />
                </div>

                <Text variant={"h3"} className="break-words">
                  {item.body}
                </Text>

                {item.type === "TEST" && (item as TestQuestion).options && (
                  <div className="grid grid-cols-1 gap-2">
                    {Object.entries((item as TestQuestion).options!).map(
                      ([key, value]) => (
                        <div
                          key={key}
                          className={`p-3 rounded-lg border ${
                            (item as TestQuestion).answer === key
                              ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                              : "border-gray-200 dark:border-gray-700"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="break-words">
                              <Text
                                variant="span"
                                size={"base"}
                                weight={"medium"}
                                className="mr-2"
                              >
                                {key}
                              </Text>
                              {value}
                            </div>
                            {item.answer === key && (
                              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 ml-2">
                                <Check size={16} />
                              </div>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                )}

                {item.type === "TF" && (
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                    <div className="font-medium text-sm text-gray-500 dark:text-gray-400 mb-2">
                      {t("interactive_material.questions.answer")}
                    </div>
                    <div
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                        item.answer === true
                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                          : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                      }`}
                    >
                      {item.answer === true
                        ? t("common.true")
                        : t("common.false")}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </ContentSection>
    </div>
  );
};

export default QuizQuestionTab;
